import React from 'react';
import { Link } from 'react-router-dom';
import AppFooter from './AppFooter';
import './PolicyPage.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="policy-page">
      <div className="policy-header">
        <img src="/white_wide_logo_noBG.png" alt="GallopAI" className="policy-logo" />
        <h1 className="policy-title">Privacy Policy</h1>
        <p className="policy-updated">Last Updated: 19 November, 2025</p>
      </div>
      
      <div className="policy-content">
        <div className="policy-card">
          <div className="policy-section">
            <h2 className="policy-section-title">Introduction</h2>
            <p className="policy-text">
              GallopAI ("we", "our", or "us") is committed to protecting your privacy and personal data. This privacy policy explains how we collect, process, and protect your personal information when you use our horse racing betting service.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Data Controller</h2>
            <p className="policy-text">
              GallopAI acts as the data controller for the personal information you provide to us. We are registered with the Information Commissioner's Office (ICO) and comply with UK data protection regulations, including the UK GDPR.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Information We Collect</h2>
            <h3 className="policy-subsection-title">Account Information</h3>
            <ul className="policy-list">
              <li>Name and email address</li>
              <li>Contact information</li>
            </ul>
            <h3 className="policy-subsection-title">Betfair Integration Data</h3>
            <ul className="policy-list">
              <li>Betfair API credentials and tokens</li>
              <li>Betfair account balance information</li>
              <li>Betting history and transaction records</li>
            </ul>
            <h3 className="policy-subsection-title">System Configuration Data</h3>
            <ul className="policy-list">
              <li>System settings and preferences</li>
              <li>Risk management parameters</li>
            </ul>
            <h3 className="policy-subsection-title">Usage Data</h3>
            <ul className="policy-list">
              <li>System activity logs</li>
              <li>Performance statistics</li>
              <li>Technical logs for troubleshooting</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">How We Use Your Information</h2>
            <h3 className="policy-subsection-title">Service Provision</h3>
            <ul className="policy-list">
              <li>Operating the automated betting system</li>
              <li>Processing bets through the Betfair API</li>
              <li>Maintaining your account and preferences</li>
              <li>Providing customer support</li>
            </ul>
            <h3 className="policy-subsection-title">Performance and Analytics</h3>
            <ul className="policy-list">
              <li>Analysing betting patterns and performance</li>
              <li>Improving our algorithms and service</li>
              <li>Generating performance reports</li>
            </ul>
            <h3 className="policy-subsection-title">Legal and Regulatory Compliance</h3>
            <p className="policy-text">
              Checks such as the following are carried out as part of the Betfair account setup and verification:
            </p>
            <ul className="policy-list">
              <li>Anti-money laundering (AML) checks</li>
              <li>Fraud prevention</li>
              <li>Regulatory reporting requirements</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Legal Basis for Processing</h2>
            <p className="policy-text">
              We process your personal data on the following legal grounds:
            </p>
            <ul className="policy-list">
              <li><strong>Contract:</strong> Processing necessary for the performance of our contract with you to provide the betting system service</li>
              <li><strong>Legal Obligation:</strong> Compliance with legal and regulatory requirements in the industry</li>
              <li><strong>Legitimate Interests:</strong> Improving our services, preventing fraud, and ensuring security</li>
              <li><strong>Consent:</strong> Where specifically requested for certain processing activities</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Data Sharing</h2>
            <p className="policy-text">
              We share your data with:
            </p>
            <ul className="policy-list">
              <li>Betfair (you authorise access to your Betfair account via Betfair's API)</li>
              <li>Payment processors (for subscription payments)</li>
              <li>Cloud service providers (for hosting and operations)</li>
              <li>Regulatory authorities (if/when legally required)</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Data Retention</h2>
            <p className="policy-text">
              We retain your personal data for as long as necessary to provide our services and comply with legal obligations. You can request deletion of your account data at any time, subject to legal retention requirements.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Your Rights</h2>
            <p className="policy-text">
              Under UK GDPR, you have the right to:
            </p>
            <ul className="policy-list">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing</li>
              <li>Data portability</li>
              <li>Withdraw consent</li>
            </ul>
            <p className="policy-text">
              To exercise these rights, please contact us through our{' '}
              <Link to="/contact" target="_blank" rel="noopener noreferrer" className="policy-link">
                support page
              </Link>.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Security</h2>
            <p className="policy-text">
              We implement appropriate technical and organizational measures to protect your personal data, including encryption, access controls, and regular security assessments. However, no internet transmission is completely secure, and we cannot guarantee absolute security.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Cookies and Tracking</h2>
            <p className="policy-text">
              We use essential cookies to maintain your session and preferences. We also use analytics cookies to improve our service. You can control cookie preferences through your browser settings.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Contact Us</h2>
            <p className="policy-text">
              For any privacy-related queries or to exercise your rights, please contact our Data Protection Officer through our{' '}
              <Link to="/contact" target="_blank" rel="noopener noreferrer" className="policy-link">
                support page
              </Link>.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Complaints</h2>
            <p className="policy-text">
              If you are unhappy with how we handle your data, you have the right to complain to the Information Commissioner's Office (ICO). Visit{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="policy-link">
                ico.org.uk
              </a>{' '}
              for more information.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">Policy Updates</h2>
            <p className="policy-text">
              We regularly review and update this privacy policy. Any changes will be posted on this page with an updated revision date. Please check back periodically to stay informed about how we protect your personal data.
            </p>
          </div>
        </div>
      </div>

      <AppFooter />
    </div>
  );
};

export default PrivacyPolicy;

